import { globalMessages } from '../../../shared/global-message';
import { SavedCard } from '../services/payment-method-state.service';

export type CardBrand = 'mastercard' | 'amex' | 'visa' | '';

/**
 * Detect the card brand from a raw card type string.
 */
export function getCardType(cardType: string): CardBrand {
  const type = cardType?.toLowerCase() || '';
  if (type.includes(globalMessages.cardTypeDetectMaster)) return globalMessages.cardBrandMastercard;
  if (
    type.includes(globalMessages.cardTypeDetectAmex) ||
    type.includes(globalMessages.cardTypeDetectAmerican)
  )
    return globalMessages.cardBrandAmex;
  if (type.includes(globalMessages.cardTypeDetectVisa)) return globalMessages.cardBrandVisa;
  return '';
}

/**
 * Get the logo asset path for a card brand.
 */
export function getCardLogo(cardType: CardBrand): string {
  const logos: Record<CardBrand, string> = {
    mastercard: globalMessages.logoMastercard,
    amex: globalMessages.logoAmex,
    visa: globalMessages.logoVisa,
    '': '',
  };
  return logos[cardType];
}

/**
 * Check whether a card's expiry date is in the past.
 */
export function isCardExpired(card: SavedCard | null): boolean {
  if (!card || !card.expiryDate || card.expiryDate === globalMessages.expiryDateNA) {
    return false;
  }

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const expirationYear = card.cardExpirationYear ?? 0;
  const expirationMonth = card.cardExpirationMonth ?? 0;
  if (expirationYear > 0 && currentYear > expirationYear) {
    return true;
  } else if (
    expirationYear > 0 &&
    currentYear == expirationYear &&
    currentMonth > expirationMonth
  ) {
    return true;
  }
  return false;
}

/**
 * Map a raw API response to SavedCard array.
 */
export function mapPaymentCardsFromResponse(response: any): SavedCard[] {
  if (!response || !response.paymentMethods) {
    return [];
  }

  return response.paymentMethods.map((card: any) => {
    const cardType = getCardType(card.creditCardType);
    const expiryDate =
      card.cardExpirationMonth && card.cardExpirationYear
        ? `${String(card.cardExpirationMonth).padStart(2, '0')}/${card.cardExpirationYear}`
        : globalMessages.expiryDateNA;

    return {
      id: card.processOneToken,
      type: cardType,
      lastFour: card.lastFour,
      cardholderName: card.customerName,
      expiryDate: expiryDate,
      isDefault: card.isDefault || false,
      logoSrc: getCardLogo(cardType),
      paymentMethodType: card.paymentMethodType,
      bankName: card.bankName,
      bankAccountType: card.bankAccountType,
      creditCardType: card.creditCardType,
      creditCardNetworkType: card.creditCardNetworkType,
      billingZipCode: card.billingZipCode || '',
      cardHolderAddress: card.cardHolderAddress || '',
      cardExpirationMonth: card.cardExpirationMonth,
      cardExpirationYear: card.cardExpirationYear,
    };
  });
}

/**
 * Determine the payment owner code.
 * 'I' when the name matches the policyholder, otherwise 'O'.
 */
export function resolvePaymentOwner(
  holderName: string | undefined,
  policyHolderName: string
): string {
  return holderName && holderName === policyHolderName
    ? globalMessages.paymentOwner
    : globalMessages.paymentOwnerOther;
}

/**
 * Replace special characters with XML entities for DC integration.
 */
export function replaceSpecialCharacters(data: string): string {
  return data.replace(/&/g, '&amp;').replace(/'/g, '&apos;').replace(/"/g, '&quot;');
}
